import { createSelector } from '@reduxjs/toolkit';

import {
  type LotterTierInProgressInfo,
  type LotteryTicketType,
  type LotteryTierInDrawInfo,
  type LotteryWinnerInfo,
} from '@/common/types/lottery';

import { type RootState } from '../store';

const selectCurrentTierName = (state: RootState): LotteryTicketType =>
  state.lottery.currentTier.name;
const selectLotteryInDraw = (state: RootState): LotteryTierInDrawInfo[] =>
  state.lottery.inDraw;
const selectLotteryInProgress = (
  state: RootState,
): LotterTierInProgressInfo[] => state.lottery.inProgress;

const selectMyTicketsByType = createSelector(
  [
    selectLotteryInProgress,
    selectLotteryInDraw,
    (_: RootState, currentTierName: LotteryTicketType): LotteryTicketType =>
      currentTierName,
  ],
  (inProgress, inDraw, currentTierName) => {
    const inProgressLottery = inProgress.find(
      ({ tier }) => tier === currentTierName,
    );
    const inDrawLottery = inDraw.find(({ tier }) => tier === currentTierName);
    const currentLottery = inProgressLottery || inDrawLottery;

    if (currentLottery) {
      return currentLottery.myTickets;
    }
  },
);

const selectCurrentInProgressLoteryData = createSelector(
  [selectCurrentTierName, selectLotteryInProgress],
  (curretnTierName, inProgress): undefined | LotterTierInProgressInfo =>
    inProgress.find(({ tier }) => tier === curretnTierName),
);

const selectCurrentCommonPool = createSelector(
  selectCurrentInProgressLoteryData,
  (currentTier) =>
    currentTier
      ? Object.values(currentTier.rewards).reduce(
          (sum, reward) => sum + (reward.tgem ?? 0),
          0,
        )
      : undefined,
);

const selectCurrentLotteryWinnerList = createSelector(
  [selectLotteryInDraw, selectCurrentTierName],
  (inDraw, currentTierName): LotteryWinnerInfo[] | undefined => {
    const currentLottery = inDraw.find(({ tier }) => tier === currentTierName);

    if (currentLottery) {
      return currentLottery.winnerList;
    }
  },
);

const selectLastLotteryWinner = createSelector(
  selectCurrentLotteryWinnerList,
  (winnerList): LotteryWinnerInfo | undefined =>
    winnerList ? winnerList[0] : undefined,
);

const selectTicketFromLastLotteryWinner = createSelector(
  selectLastLotteryWinner,
  (lastWinner): string | undefined =>
    lastWinner ? lastWinner.ticket : undefined,
);

export {
  selectCurrentCommonPool,
  selectCurrentInProgressLoteryData,
  selectCurrentLotteryWinnerList,
  selectLastLotteryWinner,
  selectMyTicketsByType,
  selectTicketFromLastLotteryWinner,
};
